import { API_BASE_URL } from '../config/api';
import { ensureMp4Faststart } from './mp4Faststart';

interface GenerateUploadUriResponse {
  uploadUrl: string;
  location: string;
}

// Content-Type fallbacks used when the browser does not provide a MIME type
// (video/audio files very often come back with an empty `file.type`). Mirrors
// the extensions recognised in the upload UI so blobs get a sensible type.
const AUDIO_EXTENSIONS = [
  'mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'flac', 'wma', 'opus', 'weba',
  'mka', 'm4b', 'aiff', 'aif',
];
const VIDEO_EXTENSIONS = [
  'mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', 'wmv', 'flv', 'mpg', 'mpeg',
  'mpe', '3gp', '3g2', 'ts', 'm2ts', 'ogv', 'f4v',
];
const EXTENSION_CONTENT_TYPES: Record<string, string> = {
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  oga: 'audio/ogg',
  m4a: 'audio/mp4',
  m4b: 'audio/mp4',
  aac: 'audio/aac',
  flac: 'audio/flac',
  wma: 'audio/x-ms-wma',
  opus: 'audio/opus',
  weba: 'audio/webm',
  aif: 'audio/x-aiff',
  aiff: 'audio/x-aiff',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  webm: 'video/webm',
  wmv: 'video/x-ms-wmv',
  flv: 'video/x-flv',
  mpg: 'video/mpeg',
  mpeg: 'video/mpeg',
  mpe: 'video/mpeg',
  '3gp': 'video/3gpp',
  '3g2': 'video/3gpp2',
  ts: 'video/mp2t',
  m2ts: 'video/mp2t',
  ogv: 'video/ogg',
};

const getFileExtension = (fileName: string): string => {
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  return ext;
};

/** Resolve a file's media kind ('audio' | 'video') from MIME type, falling back
 * to the file extension when the MIME type is empty or non-standard. */
export const getMediaFileType = (
  file: Pick<File, 'type' | 'name'>
): 'audio' | 'video' | null => {
  const mime = file.type.split('/')[0];
  if (mime === 'audio' || mime === 'video') return mime;

  const ext = getFileExtension(file.name);
  if (AUDIO_EXTENSIONS.includes(ext)) return 'audio';
  if (VIDEO_EXTENSIONS.includes(ext)) return 'video';
  return null;
};

/** Best-effort Content-Type for an uploaded blob, honouring the browser's MIME
 * type when present and falling back to the extension map otherwise. */
const resolveContentType = (blob: Blob, file: { name: string }): string => {
  if (blob.type) return blob.type;
  const ext = getFileExtension(file.name);
  return EXTENSION_CONTENT_TYPES[ext] || 'application/octet-stream';
};

/**
 * Request a signed upload URI for an audio/video asset so the blob is stored
 * against the given video id (`videos/<videoId>/assets/<fileName>`).
 *
 * Mirrors the character/audio storage flows: the frontend requests a signed
 * upload URI via `getUploadURI`, PUTs the raw blob to it, and keeps the
 * returned relative blob `location` in frontend state. Playback/resolution
 * re-signs that path with the video's SAS token (`getAssetDownloadUrl`).
 */
export const getUploadURI = async (
  fileName: string,
  videoId: string,
  token: string
): Promise<GenerateUploadUriResponse> => {
  const url = `${API_BASE_URL}/storage/generateUploadUrl?fileName=${encodeURIComponent(
    fileName
  )}&videoId=${encodeURIComponent(videoId)}`;

  console.log('[UploadAsset] generateUploadUrl request:', {
    fileName,
    videoId,
    hasToken: !!token,
    apiBaseUrl: API_BASE_URL,
  });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  console.log('[UploadAsset] generateUploadUrl response:', {
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
  });

  if (!response.ok) {
    throw new Error(`Failed to generate upload URL: ${response.statusText}`);
  }

  const data = (await response.json()) as GenerateUploadUriResponse;
  console.log('[UploadAsset] generateUploadUrl data:', {
    hasUploadUrl: !!data.uploadUrl,
    location: data.location,
  });
  return data;
};

/** Upload the raw audio/video blob to the signed URI returned by getUploadURI. */
export const uploadAssetBlob = async (
  blob: Blob,
  uploadUrl: string,
  file?: { name: string }
): Promise<boolean> => {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': resolveContentType(blob, file || { name: '' }),
    },
    body: blob,
  });

  // Trim any SAS query string so credentials are never logged.
  const blobUrlPath = uploadUrl.split('?')[0];
  console.log('[UploadAsset] blob PUT response:', {
    blobUrlPath,
    status: response.status,
    statusText: response.statusText,
    ok: response.ok,
    contentType: resolveContentType(blob, file || { name: '' }),
  });

  if (!response.ok) {
    throw new Error(`Failed to upload asset: ${response.statusText}`);
  }

  return true;
};

/** MP4-family containers eligible for moov-first normalization at upload. */
const FASTSTART_EXTENSIONS = ['mp4', 'mov', 'm4v'];

/**
 * Normalize MP4-family uploads to moov-first so playback reaches metadata
 * from the file head on slow links. Already-fast and non-MP4 files pass
 * through untouched; normalization never fails an upload.
 */
const normalizeForUpload = async (file: File): Promise<Blob> => {
  if (!FASTSTART_EXTENSIONS.includes(getFileExtension(file.name))) return file;
  try {
    const { buffer, applied, reason, atomMap } = ensureMp4Faststart(
      await file.arrayBuffer()
    );
    console.log('[UploadAsset] faststart:', {
      file: file.name,
      applied,
      reason,
      atomMap,
    });
    if (!applied) return file;
    return new Blob([buffer], {
      type: file.type || EXTENSION_CONTENT_TYPES[getFileExtension(file.name)] || 'video/mp4',
    });
  } catch (error) {
    console.warn(
      '[UploadAsset] faststart skipped, uploading original bytes:',
      (error as Error)?.message
    );
    return file;
  }
};

/**
 * Upload an audio/video asset against a video id and return the relative blob
 * location (e.g. `videos/<videoId>/assets/<fileName>`). The caller keeps this
 * location in frontend state so it can be re-signed with the video's SAS token
 * whenever the asset needs to be played back.
 */
export const uploadAssetToVideo = async (
  file: File,
  videoId: string,
  token: string
): Promise<string> => {
  const { uploadUrl, location } = await getUploadURI(file.name, videoId, token);

  await uploadAssetBlob(await normalizeForUpload(file), uploadUrl, file);

  return location;
};
